import React, { useState } from "react";
import { MenuItem, Popover, TextField } from "@material-ui/core";
import { BulkBarBtn, BulkBarDanger, BulkBarDivider, BulkBarLabel, BulkBarWrap } from "../styles";
import { LABELS } from "../labels";
import type { StationsTextEntry } from "../types";

interface Props {
	selectedCount: number;
	onApplyStyle: (patch: Partial<StationsTextEntry["stylePreset"] & { colorToken: string }>) => void;
	onAddTag: (tag: string) => void;
	onRemoveTag: (tag: string) => void;
	onPublishSelected: () => void;
	onClear: () => void;
	publishing: boolean;
}

const STYLE_PRESETS = [
	{ label: "sm / normal", patch: { fontSizePreset: "sm", fontWeight: "normal", fontStyle: "normal" } },
	{ label: "md / normal", patch: { fontSizePreset: "md", fontWeight: "normal", fontStyle: "normal" } },
	{ label: "lg / bold", patch: { fontSizePreset: "lg", fontWeight: "bold", fontStyle: "normal" } },
	{ label: "md / bold", patch: { fontSizePreset: "md", fontWeight: "bold", fontStyle: "normal" } },
	{ label: "md / italic", patch: { fontSizePreset: "md", fontWeight: "normal", fontStyle: "italic" } },
];

export default function BulkActionBar({
	selectedCount,
	onApplyStyle,
	onAddTag,
	onRemoveTag,
	onPublishSelected,
	onClear,
	publishing,
}: Props) {
	const [styleAnchor, setStyleAnchor] = useState<HTMLElement | null>(null);
	const [tagMode, setTagMode] = useState<"add" | "remove" | null>(null);
	const [tagAnchor, setTagAnchor] = useState<HTMLElement | null>(null);
	const [tagInput, setTagInput] = useState("");

	if (selectedCount === 0) return null;

	function handleTagCommit() {
		const tag = tagInput.trim().toLowerCase();
		if (tag && tagMode) {
			if (tagMode === "add") onAddTag(tag);
			else onRemoveTag(tag);
		}
		setTagAnchor(null);
		setTagMode(null);
		setTagInput("");
	}

	return (
		<>
			<BulkBarWrap>
				<BulkBarLabel>{LABELS.bulkSelected(selectedCount)}</BulkBarLabel>
				<BulkBarDivider />

				<BulkBarBtn onClick={(e) => setStyleAnchor(e.currentTarget)}>
					<i className="fa fa-paint-brush" /> {LABELS.bulkApplyStyle}
				</BulkBarBtn>

				<BulkBarBtn
					onClick={(e) => {
						setTagMode("add");
						setTagAnchor(e.currentTarget);
						setTagInput("");
					}}>
					<i className="fa fa-tag" /> {LABELS.bulkAddTag}
				</BulkBarBtn>

				<BulkBarBtn
					onClick={(e) => {
						setTagMode("remove");
						setTagAnchor(e.currentTarget);
						setTagInput("");
					}}>
					<i className="fa fa-times" /> {LABELS.bulkRemoveTag}
				</BulkBarBtn>

				<BulkBarDivider />

				<BulkBarBtn
					onClick={onPublishSelected}
					disabled={publishing}
					style={{ opacity: publishing ? 0.6 : 1 }}>
					<i className="fa fa-cloud-upload" /> {LABELS.bulkPublishSelected}
				</BulkBarBtn>

				<BulkBarDivider />

				<BulkBarDanger onClick={onClear}>
					<i className="fa fa-times-circle" /> {LABELS.bulkClear}
				</BulkBarDanger>
			</BulkBarWrap>

			{/* Style preset popover */}
			<Popover
				open={Boolean(styleAnchor)}
				anchorEl={styleAnchor}
				onClose={() => setStyleAnchor(null)}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				transformOrigin={{ vertical: "bottom", horizontal: "center" }}>
				<div style={{ padding: "8px 0", minWidth: 180 }}>
					{STYLE_PRESETS.map((preset) => (
						<MenuItem
							key={preset.label}
							onClick={() => {
								onApplyStyle(preset.patch as any);
								setStyleAnchor(null);
							}}>
							{preset.label}
						</MenuItem>
					))}
				</div>
			</Popover>

			{/* Tag input popover */}
			<Popover
				open={Boolean(tagAnchor)}
				anchorEl={tagAnchor}
				onClose={() => {
					setTagAnchor(null);
					setTagMode(null);
				}}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				transformOrigin={{ vertical: "bottom", horizontal: "center" }}>
				<div style={{ padding: 12, display: "flex", gap: 8, alignItems: "center" }}>
					<TextField
						autoFocus
						size="small"
						variant="outlined"
						placeholder="tag-name"
						value={tagInput}
						onChange={(e) => setTagInput(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter") handleTagCommit();
							if (e.key === "Escape") {
								setTagAnchor(null);
								setTagMode(null);
							}
						}}
						style={{ width: 140 }}
					/>
					<button
						onClick={handleTagCommit}
						style={{
							padding: "6px 12px",
							border: "none",
							borderRadius: 6,
							background: "#2563eb",
							color: "#fff",
							fontSize: "0.8125rem",
							cursor: "pointer",
						}}>
						{tagMode === "add" ? "Add" : "Remove"}
					</button>
				</div>
			</Popover>
		</>
	);
}
